import { Controller } from "@hotwired/stimulus"

const FEATURED_COUNTRY_CODES = {
  united_states: "US",
  canada: "CA",
  united_kingdom: "GB",
}

const FEATURED_COUNTRY_CHOICES = Object.fromEntries(
  Object.entries(FEATURED_COUNTRY_CODES).map(([choice, code]) => [code, choice])
)

export default class extends Controller {
  static targets = [
    "choice",
    "exactCountrySection",
    "countryFallback",
    "countryFallbackSelect",
    "countryCombobox",
    "countrySearch",
    "countryCode",
    "countryListbox",
    "countryOption",
    "emailOptInCheckbox",
  ]

  connect() {
    this.currentChoice = this.choiceTarget.value
    this.activeOptionIndex = -1
    this.preserveFallbackCountrySelection()
    this.countryCodeTarget.disabled = false
    this.countryFallbackSelectTarget.disabled = true
    this.countryFallbackTarget.classList.add("hidden")
    this.countryComboboxTarget.classList.remove("hidden")
    this.updatePresentation({ resetEmailChoice: false, clearCountry: false })
  }

  choiceChanged() {
    const choiceChanged = this.choiceTarget.value !== this.currentChoice
    this.updatePresentation({ resetEmailChoice: choiceChanged, clearCountry: choiceChanged })
    this.currentChoice = this.choiceTarget.value
  }

  updatePresentation({ resetEmailChoice, clearCountry }) {
    const choice = this.choiceTarget.value
    const isUnitedStates = choice === "united_states"
    const isAnotherCountry = choice === "another_country"

    if (resetEmailChoice) this.emailOptInCheckboxTarget.checked = isUnitedStates
    if (clearCountry) this.clearExactCountry()

    this.exactCountrySectionTarget.classList.toggle("hidden", !isAnotherCountry)
    this.countrySearchTarget.required = isAnotherCountry

    if (Object.hasOwn(FEATURED_COUNTRY_CODES, choice)) {
      this.countryCodeTarget.value = FEATURED_COUNTRY_CODES[choice]
    } else if (!isAnotherCountry) {
      this.countryCodeTarget.value = ""
    }

    this.updateCountryValidity()
    if (!isAnotherCountry) this.closeCountryList()
  }

  openCountryList() {
    this.countryListboxTarget.classList.remove("hidden")
    this.countrySearchTarget.setAttribute("aria-expanded", "true")
  }

  focusCountryList() {
    this.filterCountryOptions()
    this.openCountryList()
  }

  searchCountries() {
    this.countryCodeTarget.value = ""
    this.updateCountryValidity()
    this.filterCountryOptions()
    this.openCountryList()
  }

  countryFocusOut(event) {
    if (event.relatedTarget && this.countryListboxTarget.contains(event.relatedTarget)) return

    this.closeCountryList()
  }

  navigateCountries(event) {
    const visibleOptions = this.visibleCountryOptions

    if (event.key === "Escape") {
      event.preventDefault()
      this.closeCountryList()
      this.countrySearchTarget.focus()
      return
    }

    if (event.key === "Enter" && this.activeOptionIndex >= 0) {
      event.preventDefault()
      this.chooseOption(visibleOptions[this.activeOptionIndex])
      return
    }

    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return

    event.preventDefault()
    this.openCountryList()
    if (visibleOptions.length === 0) return

    if (event.key === "Home") {
      this.activeOptionIndex = 0
    } else if (event.key === "End") {
      this.activeOptionIndex = visibleOptions.length - 1
    } else {
      const direction = event.key === "ArrowDown" ? 1 : -1
      const nextIndex = this.activeOptionIndex + direction
      this.activeOptionIndex = Math.min(Math.max(nextIndex, 0), visibleOptions.length - 1)
    }

    this.activateOption(visibleOptions)
  }

  selectCountry(event) {
    event.preventDefault()
    this.chooseOption(event.currentTarget)
  }

  clearCountry(event) {
    event.preventDefault()
    this.clearExactCountry()
    this.countrySearchTarget.focus()
    this.openCountryList()
  }

  dismissCountryList(event) {
    if (!this.element.contains(event.target)) this.closeCountryList()
  }

  filterCountryOptions() {
    const query = this.countrySearchTarget.value.trim().toLocaleLowerCase()

    this.countryOptionTargets.forEach((option) => {
      const matches = option.dataset.countryName.toLocaleLowerCase().includes(query)
      option.classList.toggle("hidden", !matches)
      option.classList.remove("bg-ec-soft-surface")
      option.setAttribute("aria-selected", option.dataset.countryCode === this.countryCodeTarget.value ? "true" : "false")
      option.tabIndex = -1
    })

    this.activeOptionIndex = -1
    this.countrySearchTarget.removeAttribute("aria-activedescendant")
  }

  activateOption(visibleOptions) {
    visibleOptions.forEach((option, index) => {
      option.classList.toggle("bg-ec-soft-surface", index === this.activeOptionIndex)
    })

    const activeOption = visibleOptions[this.activeOptionIndex]
    if (!activeOption) return

    this.countrySearchTarget.setAttribute("aria-activedescendant", activeOption.id)
    activeOption.scrollIntoView({ block: "nearest" })
  }

  chooseOption(option) {
    if (!option) return

    const featuredChoice = FEATURED_COUNTRY_CHOICES[option.dataset.countryCode]
    if (featuredChoice) {
      this.choiceTarget.value = featuredChoice
      this.updatePresentation({ resetEmailChoice: true, clearCountry: true })
      this.currentChoice = featuredChoice
      this.choiceTarget.focus()
      return
    }

    this.countryCodeTarget.value = option.dataset.countryCode
    this.countrySearchTarget.value = option.dataset.countryName
    this.updateCountryValidity()
    this.filterCountryOptions()
    this.countrySearchTarget.focus()
    this.closeCountryList()
  }

  clearExactCountry() {
    this.countryCodeTarget.value = ""
    this.countrySearchTarget.value = ""
    this.updateCountryValidity()
    this.filterCountryOptions()
  }

  preserveFallbackCountrySelection() {
    if (this.currentChoice !== "another_country") return

    const fallbackCountryCode = this.countryFallbackSelectTarget.value
    if (!fallbackCountryCode) return

    const selectedOption = this.countryFallbackSelectTarget.selectedOptions[0]
    this.countryCodeTarget.value = fallbackCountryCode
    this.countrySearchTarget.value = selectedOption?.textContent.trim() || ""
  }

  updateCountryValidity() {
    const missingSelection = this.choiceTarget.value === "another_country" && this.countryCodeTarget.value === ""
    this.countrySearchTarget.setCustomValidity(missingSelection ? "Choose a country from the list." : "")
  }

  closeCountryList() {
    this.countryListboxTarget.classList.add("hidden")
    this.countrySearchTarget.setAttribute("aria-expanded", "false")
    this.countrySearchTarget.removeAttribute("aria-activedescendant")
    this.countryOptionTargets.forEach((option) => option.classList.remove("bg-ec-soft-surface"))
    this.activeOptionIndex = -1
  }

  get visibleCountryOptions() {
    return this.countryOptionTargets.filter((option) => !option.classList.contains("hidden"))
  }
}
